import { memo } from 'react';
import {
  Box,
  Paper,
  Avatar,
  Button,
  TextField,
  Typography,
  Chip,
  InputAdornment,
} from '@mui/material';
import { CheckCircle as AddedIcon } from '@mui/icons-material';
import { renderIcon } from '../IconPicker';

// Helper to get initials from a name
const getInitials = (name?: string): string => {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

export interface PlayerAddRowProps {
  id: string;
  name: string;
  handle?: string;
  iconName?: string;
  isOnline?: boolean; // true for online players, false for offline
  isOnBoard: boolean;
  justAdded: boolean;
  isAdding: boolean;
  squaresValue: number;
  balanceValue: number;
  onSquaresChange: (value: number) => void;
  onBalanceChange: (value: number) => void;
  onAdd: () => void;
  unitLabel: string; // "Squares" or "Cells"
  totalCells: number;
}

export const PlayerAddRow = memo<PlayerAddRowProps>(({
  id,
  name,
  handle,
  iconName,
  isOnBoard,
  justAdded,
  isAdding,
  squaresValue,
  balanceValue,
  onSquaresChange,
  onBalanceChange,
  onAdd,
  unitLabel,
  totalCells,
}) => {
  const showInputs = !isOnBoard && !justAdded;
  const isAdded = isOnBoard || justAdded;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isAdding) {
      e.preventDefault();
      onAdd();
    }
  };

  return (
    <Paper
      elevation={0}
      data-testid={`player-add-row-${id}`}
      data-status={isAdded ? 'added' : 'not-added'}
      sx={{
        p: 2,
        border: 1,
        borderColor: justAdded ? 'success.main' : 'divider',
        bgcolor: justAdded ? 'success.50' : 'background.paper',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
          <Avatar sx={{ bgcolor: 'secondary.main', width: 36, height: 36 }}>
            {iconName ? renderIcon(iconName, 20) : getInitials(name)}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight="bold">
              {name}
            </Typography>
            {handle && (
              <Typography variant="caption" color="text.secondary">
                @{handle}
              </Typography>
            )}
          </Box>
        </Box>
        {isAdded ? (
          <Chip label="Added" size="small" color="success" variant="outlined" icon={<AddedIcon />} data-testid="added-chip" />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {showInputs && (
              <>
                <TextField
                  size="small"
                  type="number"
                  label={unitLabel}
                  value={squaresValue}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    onSquaresChange(isNaN(val) ? 1 : Math.min(totalCells, Math.max(0, val)));
                  }}
                  onKeyDown={handleKeyDown}
                  disabled={isAdding}
                  sx={{ width: 80 }}
                  inputProps={{ min: 0, max: totalCells }}
                />
                <TextField
                  size="small"
                  type="number"
                  label="Balance"
                  value={balanceValue}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value);
                    onBalanceChange(isNaN(val) ? 0 : val);
                  }}
                  onKeyDown={handleKeyDown}
                  disabled={isAdding}
                  sx={{ width: 90 }}
                  inputProps={{ min: 0, step: 0.01 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </>
            )}
            <Button
              size="small"
              variant="contained"
              onClick={onAdd}
              disabled={isAdding}
              data-testid="add-player-button"
            >
              Add
            </Button>
          </Box>
        )}
      </Box>
    </Paper>
  );
});

PlayerAddRow.displayName = 'PlayerAddRow';
